import { useState, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { api, useAuth } from '../context/AuthContext'

const INP = { height: 44, borderRadius: 9, border: '1px solid #e9e9e4', padding: '0 14px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans',sans-serif", background: '#fff' }
const LBL = { display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5, fontFamily: "'JetBrains Mono',monospace" }

const CATEGORIES = ['Fashion','Electronics','Food & Drinks','Health & Beauty','Home & Living','Sports','Books','Art & Crafts','Groceries','Other']

export default function Register() {
  const { login } = useAuth()
  const navigate  = useNavigate()
  const [step, setStep]   = useState(1)
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState('')

  // Step 1 — account
  const [username,  setUsername]  = useState('')
  const [password,  setPassword]  = useState('')
  const [confirm,   setConfirm]   = useState('')
  const [phone,     setPhone]     = useState('')
  const [email,     setEmail]     = useState('')
  const [showPass,  setShowPass]  = useState(false)

  // Step 2 — shop
  const [shopName,  setShopName]  = useState('')
  const [category,  setCategory]  = useState('')
  const [desc,      setDesc]      = useState('')
  const [shopPhone, setShopPhone] = useState('')
  const [location,  setLocation]  = useState('')

  // Temp buyer token from step 1, used to create shop in step 2
  const buyerTokenRef = useRef(null)

  const handleAccount = async (e) => {
    e.preventDefault()
    setError('')
    if (!username.trim())          { setError('Username is required.'); return }
    if (username.trim().length < 3){ setError('Username must be at least 3 characters.'); return }
    if (!password)                 { setError('Password is required.'); return }
    if (password.length < 6)       { setError('Password must be at least 6 characters.'); return }
    if (password !== confirm)      { setError('Passwords do not match.'); return }
    if (!email.trim())             { setError('Email is required.'); return }
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) { setError('Enter a valid email address.'); return }
    if (!phone.trim())             { setError('Phone number is required.'); return }

    setSaving(true)
    try {
      const res = await api.post('/user/register', {
        username: username.trim(),
        password,
        email: email.trim(),
        phone: phone.trim(),
      })
      buyerTokenRef.current = res.data.token
      setShopPhone(phone.trim())
      setStep(2)
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.')
    } finally { setSaving(false) }
  }

  const handleShop = async (e) => {
    e.preventDefault()
    setError('')
    if (!shopName.trim()) { setError('Shop name is required.'); return }

    setSaving(true)
    try {
      const fd = new FormData()
      fd.append('name',        shopName.trim())
      fd.append('description', desc.trim())
      fd.append('category',    category)
      fd.append('phone',       shopPhone.trim())
      fd.append('location',    location.trim())

      const res = await api.post('/shops/add', fd, {
        headers: {
          'Content-Type':  'multipart/form-data',
          'Authorization': `Bearer ${buyerTokenRef.current}`,
        },
      })

      const sellerToken = res.data.token
      if (!sellerToken || !login(sellerToken)) {
        setError('Shop created but login failed — please log in manually.')
        navigate('/login')
        return
      }
      navigate('/dashboard')
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create shop. Please try again.')
    } finally { setSaving(false) }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F6F6F3', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      <div style={{ width: '100%', maxWidth: 460 }}>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32, justifyContent: 'center' }}>
          <img src="/kudos_logo.png" alt="Kudos Live" style={{ width: 44, height: 44, borderRadius: 12, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(145deg,#E8440A 0%,#c9380a 100%)', display: 'none', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(232,68,10,0.3)' }}>
            <span style={{ fontSize: 20, fontWeight: 900, color: '#fff' }}>K</span>
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.03em', color: '#111317' }}>Kudos Live</div>
            <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Seller Portal</div>
          </div>
        </div>

        {/* Card */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e9e9e4', boxShadow: '0 4px 32px rgba(0,0,0,0.07)', overflow: 'hidden' }}>

          {/* Step indicator */}
          <div style={{ padding: '20px 28px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
            {[1, 2].map(s => (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800,
                  background: step >= s ? '#E8440A' : '#f3f3f0', color: step >= s ? '#fff' : '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                  {step > s ? '✓' : s}
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: step >= s ? '#111317' : '#9a9a94' }}>
                  {s === 1 ? 'Your Account' : 'Your Shop'}
                </span>
                {s < 2 && <span style={{ color: '#d1d5db', fontSize: 14, marginLeft: 2 }}>→</span>}
              </div>
            ))}
          </div>

          <div style={{ padding: '20px 28px 28px' }}>
            <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4, letterSpacing: '-0.3px' }}>
              {step === 1 ? 'Create your account' : 'Set up your shop'}
            </div>
            <div style={{ fontSize: 13, color: '#9a9a94', marginBottom: 22 }}>
              {step === 1 ? 'Fill in your login details to get started.' : 'Your shop is how buyers find you on Kudos Live.'}
            </div>

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>
                ⚠ {error}
              </div>
            )}

            {/* ── Step 1: Account ── */}
            {step === 1 && (
              <form onSubmit={handleAccount} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={LBL}>Username *</label>
                  <input value={username} onChange={e => setUsername(e.target.value)} placeholder="e.g. jane_shop" style={INP} autoFocus autoComplete="username" />
                </div>
                <div>
                  <label style={LBL}>Phone *</label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+250 7XX XXX XXX" style={INP} type="tel" autoComplete="tel" />
                </div>
                <div>
                  <label style={LBL}>Email *</label>
                  <input value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={INP} type="email" autoComplete="email" />
                </div>
                <div>
                  <label style={LBL}>Password *</label>
                  <div style={{ position: 'relative' }}>
                    <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 characters"
                      type={showPass ? 'text' : 'password'} style={{ ...INP, paddingRight: 44 }} autoComplete="new-password" />
                    <span onClick={() => setShowPass(p => !p)}
                      style={{ position: 'absolute', right: 13, top: 12, cursor: 'pointer', fontSize: 14, color: '#9a9a94', userSelect: 'none' }}>
                      {showPass ? '🙈' : '👁'}
                    </span>
                  </div>
                </div>
                <div>
                  <label style={LBL}>Confirm Password *</label>
                  <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password"
                    type={showPass ? 'text' : 'password'} style={{ ...INP, borderColor: confirm && confirm !== password ? '#fecaca' : '#e9e9e4' }} autoComplete="new-password" />
                  {confirm && confirm !== password && (
                    <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}>Passwords don't match</div>
                  )}
                </div>
                <button type="submit" disabled={saving}
                  style={{ height: 46, borderRadius: 10, border: 'none', background: saving ? '#f0c4b4' : '#E8440A', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", marginTop: 4 }}>
                  {saving ? 'Creating account…' : 'Continue →'}
                </button>
              </form>
            )}

            {/* ── Step 2: Shop ── */}
            {step === 2 && (
              <form onSubmit={handleShop} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={LBL}>Shop Name *</label>
                  <input value={shopName} onChange={e => setShopName(e.target.value)} placeholder="e.g. Kigali Boutique" style={INP} autoFocus />
                </div>
                <div>
                  <label style={LBL}>Category</label>
                  <select value={category} onChange={e => setCategory(e.target.value)} style={{ ...INP, cursor: 'pointer' }}>
                    <option value="">Select a category…</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={LBL}>Description</label>
                  <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Tell buyers what you sell…" rows={3}
                    style={{ ...INP, height: 'auto', padding: '10px 14px', resize: 'vertical' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={LBL}>Shop Phone</label>
                    <input value={shopPhone} onChange={e => setShopPhone(e.target.value)} placeholder="078XXXXXXX" style={INP} type="tel" />
                  </div>
                  <div>
                    <label style={LBL}>Location</label>
                    <input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Kigali" style={INP} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  <button type="button" onClick={() => { setStep(1); setError('') }}
                    style={{ flex: 1, height: 46, borderRadius: 10, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", color: '#374151' }}>
                    ← Back
                  </button>
                  <button type="submit" disabled={saving}
                    style={{ flex: 2, height: 46, borderRadius: 10, border: 'none', background: saving ? '#f0c4b4' : '#E8440A', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                    {saving ? 'Creating shop…' : '🏪 Launch My Shop'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Login link */}
        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#9a9a94' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#E8440A', fontWeight: 700, textDecoration: 'none' }}>Sign in</Link>
        </div>
      </div>
    </div>
  )
}
